import React, {useState, useEffect} from 'react';
import ReactMarkdown from 'react-markdown';
import {
    MessageSquare,
    Download,
    Star,
    Loader2,
    Copy,
    AlertTriangle,
    RefreshCw,
    CheckCircle,
    XCircle,
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    X
} from 'lucide-react';

const PAGE_SIZE = 10;

const ViewChatsPage = () => {
    const [conversations, setConversations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [copiedId, setCopiedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [starUpdating, setStarUpdating] = useState({});

    // Language selection modal state
    const [languageModal, setLanguageModal] = useState({
        isOpen: false,
        chatId: null,
        currentStatus: false
    });
    const [selectedLanguage, setSelectedLanguage] = useState('en');

    useEffect(() => {
        fetchChats(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const fetchChats = async (targetPage = 1) => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(
                `https://api.digitalaalim.com/admin/conversations/?page=${targetPage}&page_size=${PAGE_SIZE}`,
                {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`}
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch chats: ${response.statusText}`);
            }

            const data = await response.json();
            // Expecting { items, page, page_size, total_items, total_pages, has_next, has_prev }
            setConversations(data.items || []);
            setPage(data.page || targetPage);
            setTotalPages(data.total_pages || 1);
            setTotalItems(data.total_items || 0);
        } catch (error) {
            console.error('Error fetching chats:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteChat = async (chatId) => {
        const confirmed = window.confirm('Are you sure you want to delete this chat?');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/delete_conversation/${chatId}`, {
                method: 'DELETE',
                headers: {Authorization: `Bearer ${accessToken}`}
            });

            if (!response.ok) {
                throw new Error(`Failed to delete chat: ${response.statusText}`);
            }

            // Re-fetch current page to keep pagination accurate
            await fetchChats(page);
        } catch (error) {
            console.error('Error deleting chat:', error);
            alert(`Failed to delete chat: ${error.message}`);
        }
    };

    // Open language selection modal
    const openLanguageModal = (chatId, currentStatus) => {
        setLanguageModal({isOpen: true, chatId, currentStatus});
        setSelectedLanguage('en'); // Reset to default
    };

    // Close language selection modal
    const closeLanguageModal = () => {
        setLanguageModal({isOpen: false, chatId: null, currentStatus: false});
        setSelectedLanguage('en');
    };

    // Confirm and toggle starred status with selected language
    const confirmToggleStarred = async () => {
        const {chatId, currentStatus} = languageModal;

        if (!selectedLanguage) {
            alert('Please select a language');
            return;
        }

        setStarUpdating(prev => ({...prev, [chatId]: true}));
        closeLanguageModal();

        try {
            const accessToken = localStorage.getItem('accessToken');
            const newStatus = !currentStatus;

            const response = await fetch(
                `https://api.digitalaalim.com/admin/star_conversation/${chatId}?language=${encodeURIComponent(selectedLanguage)}`,
                {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({starred: newStatus})
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to update starred status: ${response.statusText}`);
            }

            setConversations(prev =>
                prev.map(chat => (chat.chat_id === chatId ? {...chat, starred: newStatus} : chat))
            );
        } catch (error) {
            console.error('Error updating starred status:', error);
            alert(`Failed to update starred status: ${error.message}`);
        } finally {
            setStarUpdating(prev => ({...prev, [chatId]: false}));
        }
    };

    const copyToClipboard = (text, chatId) => {
        const plainText = text.replace(/[*#_`]/g, '');
        navigator.clipboard.writeText(plainText)
            .then(() => {
                setCopiedId(chatId);
                setTimeout(() => setCopiedId(null), 2000);
            })
            .catch(err => {
                console.error('Failed to copy:', err);
            });
    };

    const downloadCSV = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            // Export current page (you can create a separate "export all" later if needed)
            const response = await fetch(
                `https://api.digitalaalim.com/admin/export-chats-to-csv/?page=${page}&page_size=${PAGE_SIZE}`,
                {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`}
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to download CSV: ${response.statusText}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `digital_aalim_chats_page_${page}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert(`Failed to download CSV: ${error.message}`);
        }
    };

    // Client-side filter (applies to the current page's items)
    const filteredConversations = conversations.filter(chat =>
        (chat.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chat.question || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chat.answer || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleFirst = () => setPage(1);
    const handlePrev = () => setPage(p => Math.max(1, p - 1));
    const handleNext = () => setPage(p => Math.min(totalPages, p + 1));
    const handleLast = () => setPage(totalPages);

    const goToPage = (n) => {
        if (n >= 1 && n <= totalPages && n !== page) setPage(n);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxShown = 5; // center window size
        if (totalPages <= maxShown) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }

        const start = Math.max(2, page - 1);
        const end = Math.min(totalPages - 1, page + 1);

        pages.push(1);
        if (start > 2) pages.push('…');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages - 1) pages.push('…');
        pages.push(totalPages);
        return pages;
    };

    const startItem = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, totalItems);

    // Language options (customize based on your supported languages)
    const languageOptions = [
        {value: 'en', label: 'English'},
        {value: 'ur', label: 'Urdu'},
        {value: 'ar', label: 'Arabic'},
        {value: 'hi', label: 'Hindi'},
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <MessageSquare className="text-blue-600" size={32}/>
                    View User Chats
                </h1>
                <p className="mt-2 text-gray-600">
                    Browse, search, and manage Digital Aalim user conversations
                </p>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
                        {/* Removed Results dropdown; fixed at 10 per page */}
                        <div className="relative w-full sm:w-72">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={16} className="text-gray-400"/>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search chats..."
                                className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button
                            onClick={() => fetchChats(page)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>

                        <button
                            onClick={downloadCSV}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                        >
                            <Download size={14} className="mr-1"/>
                            Export CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* Language Selection Modal */}
            {languageModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Select Language</h3>
                            <button
                                onClick={closeLanguageModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={20}/>
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            Please select the language for this conversation
                            before {languageModal.currentStatus ? 'unstarring' : 'starring'} it.
                        </p>

                        <div className="space-y-2 mb-6">
                            {languageOptions.map((lang) => (
                                <label
                                    key={lang.value}
                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                        selectedLanguage === lang.value
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="language"
                                        value={lang.value}
                                        checked={selectedLanguage === lang.value}
                                        onChange={(e) => setSelectedLanguage(e.target.value)}
                                        className="mr-3"
                                    />
                                    <span className="text-sm font-medium text-gray-900">
                                        {lang.label}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={closeLanguageModal}
                                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmToggleStarred}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chats Table */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading chats</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Answer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredConversations.length > 0 ? (
                                filteredConversations.map((chat) => (
                                    <tr key={chat.chat_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 max-w-[150px]">
                                                {chat.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div
                                                className="text-sm text-gray-900 max-h-20 overflow-y-auto max-w-[260px]">
                                                {chat.question}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div
                                                className="text-sm text-gray-900 max-h-24 overflow-y-auto prose prose-sm max-w-[360px]">
                                                <ReactMarkdown>{chat.answer}</ReactMarkdown>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500">
                                                {chat.timestamp ? new Date(chat.timestamp).toLocaleString() : '—'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-3">
                                                <button
                                                    onClick={() => openLanguageModal(chat.chat_id, chat.starred)}
                                                    className={`p-1 rounded-full ${
                                                        chat.starred ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-400 hover:text-gray-500'
                                                    }`}
                                                    disabled={starUpdating[chat.chat_id]}
                                                    title={chat.starred ? 'Unstar' : 'Star'}
                                                >
                                                    {starUpdating[chat.chat_id] ? (
                                                        <Loader2 size={18} className="animate-spin"/>
                                                    ) : (
                                                        <Star size={18} fill={chat.starred ? 'currentColor' : 'none'}/>
                                                    )}
                                                </button>

                                                <button
                                                    onClick={() => copyToClipboard(chat.answer || '', chat.chat_id)}
                                                    className="p-1 rounded-full text-blue-500 hover:text-blue-600"
                                                    title="Copy answer"
                                                >
                                                    {copiedId === chat.chat_id ? <CheckCircle size={18}/> :
                                                        <Copy size={18}/>}
                                                </button>

                                                <button
                                                    onClick={() => deleteChat(chat.chat_id)}
                                                    className="p-1 rounded-full text-red-500 hover:text-red-600"
                                                    title="Delete chat"
                                                >
                                                    <XCircle size={18}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-16 text-center text-gray-500">
                                        {searchTerm ? (
                                            <div className="flex flex-col items-center">
                                                <Search size={40} className="text-gray-400 mb-2"/>
                                                <p>No chats found matching "{searchTerm}" on this page</p>
                                                <button
                                                    onClick={() => setSearchTerm('')}
                                                    className="mt-2 text-blue-600 hover:underline"
                                                >
                                                    Clear search
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center">
                                                <MessageSquare size={40} className="text-gray-400 mb-2"/>
                                                <p>No chats available</p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination footer */}
                    <div
                        className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 bg-gray-50 border-t">
                        <div className="text-sm text-gray-600">
                            Showing <span className="font-medium">{startItem}</span>–<span
                            className="font-medium">{endItem}</span> of{' '}
                            <span className="font-medium">{totalItems}</span>
                        </div>

                        <div className="inline-flex items-center gap-1">
                            <button
                                onClick={handleFirst}
                                disabled={page === 1}
                                className="px-2 py-2 rounded-md border text-sm disabled:opacity-50 hover:bg-white"
                                title="First"
                            >
                                <ChevronsLeft size={16}/>
                            </button>
                            <button
                                onClick={handlePrev}
                                disabled={page === 1}
                                className="px-2 py-2 rounded-md border text-sm disabled:opacity-50 hover:bg-white"
                                title="Previous"
                            >
                                <ChevronLeft size={16}/>
                            </button>

                            {getPageNumbers().map((p, idx) =>
                                p === '…' ? (
                                    <span key={`ellipsis-${idx}`} className="px-3 py-2 text-sm text-gray-500">…</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => goToPage(p)}
                                        className={`px-3 py-2 rounded-md border text-sm ${
                                            p === page ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-white'
                                        }`}
                                    >
                                        {p}
                                    </button>
                                )
                            )}

                            <button
                                onClick={handleNext}
                                disabled={page === totalPages || totalItems === 0}
                                className="px-2 py-2 rounded-md border text-sm disabled:opacity-50 hover:bg-white"
                                title="Next"
                            >
                                <ChevronRight size={16}/>
                            </button>
                            <button
                                onClick={handleLast}
                                disabled={page === totalPages || totalItems === 0}
                                className="px-2 py-2 rounded-md border text-sm disabled:opacity-50 hover:bg-white"
                                title="Last"
                            >
                                <ChevronsRight size={16}/>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewChatsPage;